import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';
import { client } from '../../util/api-client';
import type {
  OrderflowSettings,
  PaymentTerms,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderSearchInput,
  ShipMethods,
  TaxCode,
} from '@repo/api-contract';
import { debounce } from '@mui/material/utils';
import { pushToast, useDispatch } from '../../state';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../modals/constants/errors';
import dayjs from 'dayjs';

type PurchaseOrderOption = PurchaseOrder | { inputValue: string };

interface Props {
  label: string;
  value: PurchaseOrder | null;
  onChange: (purchaseOrder: PurchaseOrder | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  putItems?: PurchaseOrderItem[];
}

const SelectSearchPurchaseOrder: React.FC<Props> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  disabled,
  putItems,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly PurchaseOrderOption[]>([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const searchPurchaseOrders = async (searchQuery: string) => {
    try {
      const payload: PurchaseOrderSearchInput = {
        query: searchQuery,
        searchBy: 'orderCode',
        size: 10,
        page: 0,
      };
      const resp = await client.searchPurchaseOrders.query(payload);

      let updated: PurchaseOrderOption[] = resp.results;

      // Add an "Add " option if no exact match
      const hasExactMatch: boolean = resp.results.some(
        (cust) => cust.vendorName.toLowerCase() === searchQuery.toLowerCase(),
      );

      if (!hasExactMatch && searchQuery.trim() !== '') {
        updated.push({ inputValue: searchQuery });
      }

      setOptions(updated);
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = React.useCallback(
    debounce((query: string) => {
      searchPurchaseOrders(query);
    }, 1100),
    [],
  );

  const onInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    // We only want to trigger search on actual text input
    if (reason === 'input') {
      setLoading(true);
      debouncedSearch(newInputValue);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setLoading(true);
    searchPurchaseOrders('');
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  const handleChange = async (
    event: React.SyntheticEvent,
    newValue: PurchaseOrderOption | null,
  ) => {
    if (!newValue) {
      onChange(null);
      return;
    }

    // If the user picked the "Add " item:
    if ('inputValue' in newValue) {
      setLoading(true);
      const newPurchaseOrderVendorName = newValue.inputValue;
      try {
        // ---- Default Values----
        const [shipMethodsResp, paymentTermsResp, settingsResp, code] =
          await Promise.all([
            client.allShippingMethods.query(),
            client.allPaymentTerms.query(),
            client.getSettings.query(),
            client.getSuggestedPurchaseOrderCode.query(),
          ]);

        const shipMethods = shipMethodsResp.results as ShipMethods[];
        const paymentTerms = paymentTermsResp.results as PaymentTerms[];
        const settings = settingsResp as OrderflowSettings;

        const orderDate = dayjs().unix();

        const defaultTerm = paymentTerms.find((pT) => pT.default);
        const defaultShipMethod = shipMethods.find((sM) => sM.default);

        let estimatedShipDate = 0;
        let deadline = 0;

        if (settings && settings.purchaseOrders) {
          const sD = settings.purchaseOrders.shippingDays;
          const tD = settings.purchaseOrders.trackingDays;

          if (sD && tD && sD > 0 && tD > 0) {
            estimatedShipDate = dayjs.unix(orderDate).add(sD, 'day').unix();
            deadline = dayjs
              .unix(orderDate)
              .add(sD + tD, 'day')
              .unix();
          }
        }
        // ----------------------

        // creates a new purchaseOrder
        const createdPurchaseOrder = await client.createPurchaseOrder.mutate({
          code: code,
          rate: 0,
          orderId: '',
          customerDeposit: 0,
          shipMethod: defaultShipMethod ? defaultShipMethod.ShipMethodId : '',
          inHandsDate: 0,
          vendorName: newPurchaseOrderVendorName,
          vendorAddress: '',
          shipToName: '',
          shipToAddress: '',
          deadline: deadline,
          estimatedShipDate: estimatedShipDate,
          vendorAccountNumber: '',
          items: [...(putItems || [])],
          orderDate: orderDate,
          notes: '',
          paymentTerms: defaultTerm ? [defaultTerm] : [],
        });
        // set it as the selected value
        onChange(createdPurchaseOrder);
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
      }
      setLoading(false);
    } else {
      // It's an existing purchaseOrder
      onChange(newValue);
    }
  };

  return (
    <Autocomplete
      disabled={disabled}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={value}
      disablePortal
      // Distinguish real vs add options
      isOptionEqualToValue={(option, val) => {
        // If both are "Add" placeholders, compare inputValue
        if ('inputValue' in option && 'inputValue' in val) {
          return option.inputValue === val.inputValue;
        }
        // Otherwise compare IDs or something unique
        if ('id' in option && 'id' in val) {
          return option.id === val.id;
        }
        return false;
      }}
      getOptionLabel={(option) => {
        if ('inputValue' in option) {
          return `Adding "${option.inputValue}"`;
        }
        return `${option.code} ${option.vendorName}`;
      }}
      options={options}
      loading={loading}
      onInputChange={onInputChange}
      onChange={handleChange}
      renderOption={(optionProps, option) => {
        // This is weird part. It says that key exists but its not in some cases. So we just take it out and don't use it. We generate keys later.
        const { key, ...liProps } = optionProps as {
          key?: React.Key;
        } & React.HTMLAttributes<HTMLLIElement>;

        if ('inputValue' in option) {
          return (
            <li
              key={crypto.randomUUID()}
              {...liProps}
              style={{
                whiteSpace: 'nowrap',
              }}
            >
              <span>{`Add "${option.inputValue}"`}</span>
            </li>
          );
        }
        return (
          <li
            key={crypto.randomUUID()}
            {...liProps}
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {`${option.code} ${option.vendorName}`}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SelectSearchPurchaseOrder;
